/**
 * Drilldown 视图 — 趋势图、明细表、汇总卡片
 */

import { authFetch } from './api.js';
import {
  getViewHistory,
  setCurrentDrillParams, getCurrentDrillParams,
  setCurrentDrillType,
  setDrilldownCurrentPage, getDrilldownCurrentPage,
  setDrilldownChart, getDrilldownChart,
} from './state.js';
import { showToast, formatNumber, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { resolveDashboardContext, appendDashboardQuery, formatRangeHint, periodLabel, applyDateInputs, readDateInputs } from './dashboard-context.js';
import { loadChartLibrary } from '../interaction/ui/chart-loader.js';

/** 预定义调色板（最多支持 10 个用户） */
const CHART_COLORS = [
  { bg: 'rgba(59, 130, 246, 0.6)',  border: '#3b82f6' },
  { bg: 'rgba(234, 179, 8, 0.6)',   border: '#eab308' },
  { bg: 'rgba(16, 185, 129, 0.6)',  border: '#10b981' },
  { bg: 'rgba(239, 68, 68, 0.6)',   border: '#ef4444' },
  { bg: 'rgba(168, 85, 247, 0.6)',  border: '#a855f7' },
  { bg: 'rgba(236, 72, 153, 0.6)',  border: '#ec4899' },
  { bg: 'rgba(20, 184, 166, 0.6)',  border: '#14b8a6' },
  { bg: 'rgba(249, 115, 22, 0.6)',  border: '#f97316' },
  { bg: 'rgba(99, 102, 241, 0.6)',  border: '#6366f1' },
  { bg: 'rgba(156, 163, 175, 0.6)', border: '#9ca3af' },
];

export function initDrilldown() {
  registerViewLoader('admin-drilldown', (params) => {
    const drillParams = params || getCurrentDrillParams();
    const ctx = resolveDashboardContext(drillParams);
    applyDateInputs('drilldown-start', 'drilldown-end', ctx);
    loadDrilldown(drillParams);
  });

  const applyBtn = document.getElementById('drilldown-apply-range-btn');
  if (applyBtn) {
    applyBtn.addEventListener('click', () => {
      const params = getCurrentDrillParams() || {};
      loadDrilldown({ ...params, ...readDateInputs('drilldown-start', 'drilldown-end', resolveDashboardContext(params)) });
    });
  }
}

// ==================== Helpers ====================

function getDrilldownContext(params = {}) {
  const base = resolveDashboardContext(params);
  return readDateInputs('drilldown-start', 'drilldown-end', base);
}

function showDrilldownSection(section, { showSummary = false } = {}) {
  const sections = ['chart-toolbar', 'chart-container', 'summary-cards', 'table-container'];
  sections.forEach(s => {
    const el = document.getElementById(`drilldown-${s}`);
    if (!el) return;
    const visible = s === 'summary-cards'
      ? showSummary
      : s === section || (section === 'chart-container' && s === 'chart-toolbar');
    el.hidden = !visible;
  });
}

// ==================== Table / Summary helpers ====================

function createDrilldownElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = text;
  return element;
}

function createDrilldownHeaderCell(header, { matrix = false, index = 0, total = 0 } = {}) {
  const cell = createDrilldownElement('th', 'drilldown-table-cell drilldown-table-header drilldown-table-cell--center', header);
  cell.scope = 'col';
  if (!matrix) return cell;

  cell.classList.add('drilldown-table-header--matrix');
  if (index === 0) cell.classList.add('drilldown-table-cell--sticky-start');
  if (index === total - 1) cell.classList.add('drilldown-table-cell--sticky-end');
  return cell;
}

function createDrilldownDataCell(cellValue, { matrix = false, index = 0, total = 0 } = {}) {
  const isNumeric = typeof cellValue === 'number';
  const cell = createDrilldownElement(
    'td',
    `drilldown-table-cell drilldown-table-cell--center${isNumeric ? ' drilldown-table-cell--numeric' : ''}`,
    isNumeric ? formatNumber(cellValue) : String(cellValue ?? '-')
  );
  if (!matrix) return cell;

  if (index === 0) cell.classList.add('drilldown-table-cell--sticky-start');
  if (index === total - 1) {
    cell.classList.add('drilldown-table-cell--sticky-end', 'drilldown-table-cell--total');
  }
  return cell;
}

function createDrilldownHeaderRow(headers, options) {
  const row = document.createElement('tr');
  headers.forEach((header, index) => {
    row.appendChild(createDrilldownHeaderCell(header, { ...options, index, total: headers.length }));
  });
  return row;
}

function createDrilldownEmptyRow(columnCount) {
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  cell.colSpan = columnCount;
  const state = createDrilldownElement('div', 'empty-state');
  state.append(
    createDrilldownElement('div', 'empty-icon', '📭'),
    createDrilldownElement('p', '', '暂无相关消耗数据'),
    createDrilldownElement('p', 'drilldown-empty-detail', '修复后的数据将在此处显示')
  );
  cell.appendChild(state);
  row.appendChild(cell);
  return row;
}

function createDrilldownDataRow(values, options) {
  const row = document.createElement('tr');
  values.forEach((cell, index) => {
    row.appendChild(createDrilldownDataCell(cell, { ...options, index, total: values.length }));
  });
  return row;
}

function renderDrilldownTable(headers, rows, { showSummary = false } = {}) {
  const thead = document.getElementById('drilldown-thead');
  const tbody = document.getElementById('drilldown-tbody');
  showDrilldownSection('table-container', { showSummary });

  thead.replaceChildren(createDrilldownHeaderRow(headers));

  if (!rows || rows.length === 0) {
    tbody.replaceChildren(createDrilldownEmptyRow(headers.length));
    return;
  }

  tbody.replaceChildren(...rows.map((row) => createDrilldownDataRow(row)));
}

function renderDrilldownSummaryCards(cards) {
  const container = document.getElementById('drilldown-summary-cards');
  if (!container) return;
  container.hidden = false;
  container.replaceChildren(...cards.map((card) => {
    const item = createDrilldownElement('div', 'stat-card drilldown-summary-card');
    item.append(
      createDrilldownElement('div', 'stat-card-icon', card.icon),
      createDrilldownElement('div', 'stat-card-label', card.label),
      createDrilldownElement('div', 'stat-card-value', formatNumber(card.value))
    );
    return item;
  }));
}

// ==================== Main loader ====================

export async function loadDrilldown(params = {}) {
  setCurrentDrillParams(params);
  setCurrentDrillType(params.drillType);
  setDrilldownCurrentPage(1);

  const chart = getDrilldownChart();
  chart?.destroy?.();
  setDrilldownChart(null);

  const pag = document.getElementById('drilldown-pagination');
  if (pag) pag.replaceChildren();

  switch (params.drillType) {
    case 'listing-cost':  await loadDrilldownListingCost(params); break;
    case 'user-output':   await loadDrilldownUserOutput(params); break;
    case 'monthly-matrix': await loadDrilldownMonthlyMatrix(params); break;
    case 'activity':      await loadDrilldownActivity(params); break;
    case 'trend':         await loadDrilldownTrend(params); break;
    default:
      document.getElementById('drilldown-title').textContent = '未知视图';
  }
}

// ==================== Listing 消耗对账单 ====================

async function loadDrilldownListingCost(params) {
  const sortField = params.sortField || 'calls';
  const ctx = getDrilldownContext(params);
  document.getElementById('drilldown-title').textContent = 'Listing 消耗对账单';
  document.getElementById('drilldown-subtitle').textContent = formatRangeHint(ctx) + ' · 每个 Listing 的市场数据调用与 Token（历史 Token 可能为 0）';

  try {
    const qs = appendDashboardQuery(new URLSearchParams({ type: sortField, page: getDrilldownCurrentPage(), size: 20 }), ctx);
    const resp = await authFetch(`/api/admin/drilldown/listing-cost?${qs}`);
    if (!resp.ok) throw new Error('加载失败');
    const data = await resp.json();

    renderDrilldownSummaryCards([
      { icon: '📋', label: '总 Listing 数', value: data.total || 0 },
      { icon: '🔌', label: '总市场数据调用', value: data.summary?.totalApiCalls || 0 },
      { icon: '🔋', label: '总 Token 消耗', value: data.summary?.totalTokens || 0 },
    ]);

    renderDrilldownTable(data.headers, data.rows, { showSummary: true });

    if (data.total > 20) {
      renderPagination('drilldown-pagination', data.total, data.page, 20, (p) => {
        setDrilldownCurrentPage(p);
        loadDrilldownListingCost(params);
      });
    }
  } catch (err) {
    console.error('[Drilldown] listing-cost error:', err);
    showToast('加载 Listing 消耗数据失败', 'error');
  }
}

// ==================== 有产出用户 ====================

async function loadDrilldownUserOutput(params = {}) {
  const ctx = getDrilldownContext(params);
  document.getElementById('drilldown-title').textContent = '有产出用户明细';
  document.getElementById('drilldown-subtitle').textContent = formatRangeHint(ctx) + ' · 期内新建 Listing 的用户';

  try {
    const resp = await authFetch(`/api/admin/drilldown/user-output?${appendDashboardQuery(new URLSearchParams(), ctx)}`);
    if (!resp.ok) throw new Error('加载失败');
    const data = await resp.json();

    document.getElementById('drilldown-summary-cards').hidden = true;
    renderDrilldownTable(data.headers, data.rows);
  } catch (err) {
    console.error('[Drilldown] user-output error:', err);
    showToast('加载产出用户数据失败', 'error');
  }
}

// ==================== 月度产出矩阵 ====================

async function loadDrilldownMonthlyMatrix(params = {}) {
  document.getElementById('drilldown-title').textContent = '月度产出矩阵';
  document.getElementById('drilldown-subtitle').textContent = '各用户按月的 Listing 创建数量';

  try {
    const ctx = getDrilldownContext(params);
    const resp = await authFetch(`/api/admin/drilldown/monthly-matrix?${appendDashboardQuery(new URLSearchParams(), ctx)}`);
    if (!resp.ok) throw new Error('加载失败');
    const data = await resp.json();

    document.getElementById('drilldown-summary-cards').hidden = true;

    if (!data.rows || data.rows.length === 0) {
      renderDrilldownTable(['用户名', '合计'], []);
      return;
    }

    showDrilldownSection('table-container');
    const thead = document.getElementById('drilldown-thead');
    const tbody = document.getElementById('drilldown-tbody');

    thead.replaceChildren(createDrilldownHeaderRow(data.headers, { matrix: true }));
    tbody.replaceChildren(...data.rows.map((row) => createDrilldownDataRow(row, { matrix: true })));
  } catch (err) {
    console.error('[Drilldown] monthly-matrix error:', err);
    showToast('加载月度矩阵失败', 'error');
  }
}

// ==================== 今日活跃 ====================

async function loadDrilldownActivity(params = {}) {
  const ctx = getDrilldownContext(params);
  document.getElementById('drilldown-title').textContent = ctx.period === 'today' ? '今日活跃用户' : '活跃用户';
  document.getElementById('drilldown-subtitle').textContent = formatRangeHint(ctx) + ' · 有 API 操作或登录记录';

  try {
    const resp = await authFetch(`/api/admin/drilldown/activity?${appendDashboardQuery(new URLSearchParams(), ctx)}`);
    if (!resp.ok) throw new Error('加载失败');
    const data = await resp.json();

    document.getElementById('drilldown-summary-cards').hidden = true;
    renderDrilldownTable(data.headers, data.rows);
  } catch (err) {
    console.error('[Drilldown] activity error:', err);
    showToast('加载活跃数据失败', 'error');
  }
}

// ==================== 趋势图表 ====================

async function loadDrilldownTrend(params) {
  const metricType = params.metricType || 'tokens';
  const titleMap = { tokens: 'Token 消耗趋势', images: 'LinkFox 算力趋势' };
  const title = titleMap[metricType] || 'API 调用趋势';
  document.getElementById('drilldown-title').textContent = title;
  document.getElementById('drilldown-subtitle').textContent = formatRangeHint(getDrilldownContext(params)) + ' · 可切换粒度/用户';

  showDrilldownSection('chart-container');

  await fetchAndRenderTrend('day', metricType, null, params);

  const toggle = document.getElementById('drilldown-granularity-toggle');
  if (toggle) {
    toggle.onclick = (e) => {
      const btn = e.target.closest('button[data-granularity]');
      if (!btn) return;
      toggle.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const userId = document.getElementById('drilldown-user-filter')?.value || '';
      fetchAndRenderTrend(btn.dataset.granularity, metricType, userId, params);
    };
  }

  const userSelect = document.getElementById('drilldown-user-filter');
  if (userSelect) {
    userSelect.onchange = () => {
      const granularity = toggle?.querySelector('button.active')?.dataset.granularity || 'day';
      fetchAndRenderTrend(granularity, metricType, userSelect.value, params);
    };
  }
}

async function fetchAndRenderTrend(granularity, metricType, userId, params) {
  try {
    const ctx = getDrilldownContext(params);
    const qs = appendDashboardQuery(new URLSearchParams({ granularity, type: metricType }), ctx);
    if (userId) qs.set('userId', userId);
    const resp = await authFetch(`/api/admin/drilldown/trend?${qs}`);
    if (!resp.ok) throw new Error('加载失败');
    const data = await resp.json();

    const userSelect = document.getElementById('drilldown-user-filter');
    if (userSelect && userSelect.options.length <= 1 && data.users?.length > 0) {
      data.users.forEach(u => {
        const opt = document.createElement('option');
        opt.value = u.id;
        opt.textContent = u.name;
        userSelect.appendChild(opt);
      });
    }

    const metricLabel = data.datasets?.[0]?.label || '';
    const userCount = data.datasets?.length || 0;
    const userName = userId
      ? (data.users?.find(u => String(u.id) === String(userId))?.name || '未知用户')
      : (userCount > 1 ? `全部用户（${userCount}人）` : '全部用户');
    const granularityLabel = { day: periodLabel(ctx.period), week: '按周', month: '按月' }[granularity] || '';
    document.getElementById('drilldown-subtitle').textContent = `${userName} · ${granularityLabel} · ${metricLabel}`;

    await renderDrilldownChart(data, metricLabel);
  } catch (err) {
    console.error('[Drilldown] trend error:', err);
    showToast('加载趋势数据失败', 'error');
  }
}

async function renderDrilldownChart(data, metricLabel) {
  const currentChart = getDrilldownChart();
  currentChart?.destroy?.();
  setDrilldownChart(null);

  const canvas = document.getElementById('drilldown-chart');
  if (!canvas) return;
  const container = canvas.parentElement;

  const existingMsg = container.querySelector('.drilldown-chart-empty-msg');
  if (existingMsg) existingMsg.remove();

  const hasData = (data.datasets || []).some(ds => (ds.data || []).some(v => v > 0));
  if (!data.labels?.length || !data.datasets?.length || !hasData) {
    canvas.hidden = true;
    canvas.width = 0;
    canvas.height = 0;
    const msg = document.createElement('div');
    msg.className = 'drilldown-chart-empty-msg';
    msg.setAttribute('role', 'status');
    msg.textContent = '暂无数据';
    container.appendChild(msg);
    return;
  }

  const { Chart, ChartDataLabels } = await loadChartLibrary();
  canvas.hidden = false;
  const ctx = canvas.getContext('2d');

  const datalabelsPlugin = ChartDataLabels ? [ChartDataLabels] : [];
  const isSingleDataset = (data.datasets?.length || 0) <= 1;
  const chartType = isSingleDataset ? 'bar' : 'line';

  const chartDatasets = (data.datasets || []).map((ds, i) => {
    const color = CHART_COLORS[i % CHART_COLORS.length];
    if (isSingleDataset) {
      return {
        label: ds.label,
        data: ds.data || [],
        backgroundColor: color.bg,
        borderColor: color.border,
        borderWidth: 1,
        maxBarThickness: 40,
      };
    }
    return {
      label: ds.label,
      data: ds.data || [],
      borderColor: color.border,
      backgroundColor: color.bg,
      borderWidth: 2,
      tension: 0.3,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6,
    };
  });

  const newChart = new Chart(ctx, {
    type: chartType,
    plugins: datalabelsPlugin,
    data: {
      labels: data.labels || [],
      datasets: chartDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 300,
      },
      plugins: {
        legend: {
          display: !isSingleDataset,
          position: 'top',
          labels: { color: '#a1a1aa', font: { size: 12 }, usePointStyle: true, pointStyle: 'circle' }
        },
        tooltip: {
          callbacks: {
            label: (tipCtx) => `${tipCtx.dataset.label}: ${formatNumber(tipCtx.parsed.y)}`
          }
        },
        datalabels: {
          display: isSingleDataset ? 'auto' : false,
          anchor: 'end',
          align: 'top',
          color: '#a1a1aa',
          font: { size: 11, weight: '600' },
          formatter: (value) => value > 0 ? formatNumber(value) : ''
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          stacked: false,
          ticks: { color: '#71717a', callback: (v) => formatNumber(v) },
          grid: { color: 'rgba(113,113,122,0.15)' }
        },
        x: {
          ticks: { color: '#71717a' },
          grid: { display: false }
        }
      }
    }
  });
  ctx.chart = newChart;
  setDrilldownChart(newChart);
}
